import React, { useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Form from '../../Form';
import Modal from '../../Modal';
import {
    DEFAULT_LINE_ITEM,
    InvoiceFormSection,
    InvoiceLineItemsEditor,
    InvoiceTotalsSummary,
    dollarsToCents,
    normalizeLineItems,
} from './InvoiceFormParts';

function formatDateInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export function getDefaultDueAt(now = new Date()) {
    const dueAt = new Date(now);
    dueAt.setHours(12, 0, 0, 0);
    dueAt.setDate(dueAt.getDate() + 7);

    return formatDateInputValue(dueAt);
}

function createInitialValues() {
    return {
        invoiceNumber: '',
        currency: 'USD',
        dueAt: getDefaultDueAt(),
        customerName: '',
        customerEmail: '',
        initialMessage: '',
        lineItems: [{ ...DEFAULT_LINE_ITEM }],
        taxDollars: '0.00',
        shippingDollars: '0.00',
        discountDollars: '0.00',
    };
}

function CreateInvoiceModal({ onClose, onSuccess }) {
    const { fetch: authFetch, API_URL } = useAuth();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [sendConfirmationMessage, setSendConfirmationMessage] = useState(null);
    const [sendConfirmationArmed, setSendConfirmationArmed] = useState(false);
    const [values, setValues] = useState(() => createInitialValues());

    const lineItems = values.lineItems || [];
    const taxCents = dollarsToCents(values.taxDollars);
    const shippingCents = dollarsToCents(values.shippingDollars);
    const discountCents = dollarsToCents(values.discountDollars);
    const subtotalCents = lineItems.reduce((sum, item) => (
        sum + (Number.parseInt(item.quantity, 10) || 0) * (Number.parseInt(item.unitPriceCents, 10) || 0)
    ), 0);
    const totalCents = Math.max(0, subtotalCents + taxCents + shippingCents - discountCents);

    const handleSubmit = async (submittedValues, options = {}) => {
        const { sendNow = false } = options;

        setError(null);
        setStatusMessage(null);
        setSendConfirmationMessage(null);
        setSaving(true);

        try {
            const payload = {
                invoiceNumber: submittedValues.invoiceNumber.trim() || null,
                currency: 'USD',
                lineItems: normalizeLineItems(submittedValues.lineItems),
                taxCents: dollarsToCents(submittedValues.taxDollars),
                shippingCents: dollarsToCents(submittedValues.shippingDollars),
                discountCents: dollarsToCents(submittedValues.discountDollars),
                dueAt: submittedValues.dueAt || null,
                customer: {
                    name: submittedValues.customerName.trim() || null,
                    email: submittedValues.customerEmail.trim() || null,
                },
            };

            const response = await authFetch(`${API_URL}/v1/seller/invoices`, {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            const json = await response.json();
            if (!response.ok) {
                setError(json.error || 'Failed to create invoice');
                return;
            }

            setStatusMessage(sendNow ? 'Invoice created. Sending to customer...' : 'Invoice draft saved. Refreshing list...');

            // Post initial message if provided
            const messageBody = (submittedValues.initialMessage || '').trim();
            if (messageBody && json.data?.uuid) {
                await authFetch(`${API_URL}/v1/seller/invoices/${json.data.uuid}/messages`, {
                    method: 'POST',
                    body: JSON.stringify({ body: messageBody }),
                });
            }

            let finalInvoice = json.data;

            if (sendNow && json.data?.uuid) {
                const sendResponse = await authFetch(`${API_URL}/v1/seller/invoices/${json.data.uuid}/send`, {
                    method: 'POST',
                });
                const sendJson = await sendResponse.json();

                if (!sendResponse.ok) {
                    setError(sendJson.error || 'Invoice was created, but sending failed.');
                    return;
                }

                finalInvoice = sendJson.data || finalInvoice;
                setStatusMessage('Invoice sent and customer notified. Closing...');
            }

            await new Promise((resolve) => setTimeout(resolve, 700));

            onSuccess(finalInvoice);
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDraftClick = () => {
        if (saving) return;

        setSendConfirmationArmed(false);
        setSendConfirmationMessage(null);
        handleSubmit(values, { sendNow: false });
    };

    const handleSendNowClick = () => {
        if (saving) return;

        if (!sendConfirmationArmed) {
            setSendConfirmationArmed(true);
            setSendConfirmationMessage('This will create and immediately notify your customer. Please double check all information and then click again to send.');
            return;
        }

        handleSubmit(values, { sendNow: true });
    };

    return (
        <Modal
            onClose={onClose}
            title="Create Invoice"
            isOpen={true}
            size="large"
            className="invoice-modal"
        >
            <Form
                values={values}
                onChange={setValues}
                onSubmit={(submittedValues) => handleSubmit(submittedValues, { sendNow: false })}
                className="invoice-form"
                fields={[
                    {
                        name: 'invoiceBasics',
                        type: 'render',
                        render: ({ values: formValues, onChange }) => (
                            <InvoiceFormSection
                                title="Invoice details"
                                description="Use an optional invoice number if you already have a numbering sequence."
                            >
                                <div className="invoice-form-grid invoice-form-grid--two">
                                    <Form
                                        as="div"
                                        values={formValues}
                                        onChange={(nextValues, name, value) => onChange(name, value)}
                                        className="invoice-subform"
                                        fields={[
                                            {
                                                name: 'invoiceNumber',
                                                label: 'Invoice number',
                                                placeholder: 'e.g. INV-0042',
                                                maxLength: 100,
                                            },
                                            {
                                                name: 'dueAt',
                                                type: 'date',
                                                label: 'Due date',
                                            },
                                        ]}
                                    />
                                </div>
                            </InvoiceFormSection>
                        ),
                    },
                    {
                        name: 'customer',
                        type: 'render',
                        render: ({ values: formValues, onChange }) => (
                            <InvoiceFormSection title="Customer" description="These details appear on the customer-facing invoice.">
                                <div className="invoice-form-grid invoice-form-grid--two">
                                    <Form
                                        as="div"
                                        values={formValues}
                                        onChange={(nextValues, name, value) => onChange(name, value)}
                                        className="invoice-subform"
                                        fields={[
                                            {
                                                name: 'customerName',
                                                label: 'Name',
                                                placeholder: 'Customer name',
                                                maxLength: 200,
                                            },
                                            {
                                                name: 'customerEmail',
                                                type: 'email',
                                                label: 'Email',
                                                placeholder: 'customer@example.com',
                                                maxLength: 255,
                                            },
                                        ]}
                                    />
                                </div>
                                <div className="invoice-form-grid invoice-form-grid--one" style={{ marginTop: '0.75rem' }}>
                                    <Form
                                        as="div"
                                        values={formValues}
                                        onChange={(nextValues, name, value) => onChange(name, value)}
                                        className="invoice-subform"
                                        fields={[
                                            {
                                                name: 'initialMessage',
                                                type: 'textarea',
                                                label: 'Initial message (optional)',
                                                placeholder: 'Add a note or message to the customer…',
                                                maxLength: 5000,
                                                rows: 3,
                                            },
                                        ]}
                                    />
                                </div>
                            </InvoiceFormSection>
                        ),
                    },
                    {
                        name: 'lineItems',
                        type: 'render',
                        render: ({ value, values: formValues, onChange }) => (
                            <InvoiceFormSection title="Line items" description="Add one row per item, service, or fee.">
                                <InvoiceLineItemsEditor
                                    lineItems={value}
                                    currency={formValues.currency}
                                    onChange={(nextItems) => onChange('lineItems', nextItems)}
                                />
                            </InvoiceFormSection>
                        ),
                    },
                    {
                        name: 'adjustments',
                        type: 'render',
                        render: ({ values: formValues, onChange }) => (
                            <InvoiceFormSection title="Adjustments">
                                <div className="invoice-form-grid invoice-form-grid--three">
                                    <Form
                                        as="div"
                                        values={formValues}
                                        onChange={(nextValues, name, value) => onChange(name, value)}
                                        className="invoice-subform"
                                        fields={[
                                            {
                                                name: 'taxDollars',
                                                type: 'number',
                                                label: 'Tax',
                                                min: 0,
                                                step: '0.01',
                                            },
                                            {
                                                name: 'shippingDollars',
                                                type: 'number',
                                                label: 'Shipping',
                                                min: 0,
                                                step: '0.01',
                                            },
                                            {
                                                name: 'discountDollars',
                                                type: 'number',
                                                label: 'Discount',
                                                min: 0,
                                                step: '0.01',
                                            },
                                        ]}
                                    />
                                </div>
                            </InvoiceFormSection>
                        ),
                    },
                ]}
            >
                {error && <div className="error invoice-form-alert">{error}</div>}

                <InvoiceTotalsSummary
                    subtotalCents={subtotalCents}
                    taxCents={taxCents}
                    shippingCents={shippingCents}
                    discountCents={discountCents}
                    totalCents={totalCents}
                    currency={values.currency}
                />

                {sendConfirmationMessage && <div className="info invoice-form-alert">{sendConfirmationMessage}</div>}
                {statusMessage && <div className="info invoice-form-alert">{statusMessage}</div>}

                <div className="form-actions invoice-form-actions">
                    <button type="button" className="button secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="button" className="button secondary" onClick={handleSaveDraftClick} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Draft'}
                    </button>
                    <button type="button" className="button primary" onClick={handleSendNowClick} disabled={saving}>
                        {saving ? 'Sending...' : sendConfirmationArmed ? 'Confirm Send' : 'Send Now'}
                    </button>
                </div>
            </Form>
        </Modal>
    );
}

export default CreateInvoiceModal;
